import * as React from 'react'

import { classList } from 'react-classlist-helper'

import type {
  TableFieldDef,
  TableFields,
} from '~/client/src/desktop/tables/tableFields'
import { buildTableFields } from '~/client/src/desktop/tables/tableFields'
import { Button } from '~/client/src/shared/components/Button/Button'
import { emptyIfBlank } from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import UserOrBrandCell from '../UserOrBrandCell'
import { IResourceFormRow, ResourceFormsColumnId } from './types'

interface ResourceFormRowActionHandlers {
  onEdit: (id: string) => void
}

interface ResourceFormFieldsArgs {
  readonly rowActionHandlers: ResourceFormRowActionHandlers
}

export function buildResourceFormFields({
  rowActionHandlers,
}: ResourceFormFieldsArgs): TableFields<IResourceFormRow> {
  const t = Localization.translator
  const fields: readonly TableFieldDef<IResourceFormRow>[] = [
    {
      key: ResourceFormsColumnId.Name,
      label: t.name,
      value: row => emptyIfBlank(row.name),
      searchText: row => row.name,
      sortable: true,
      groupable: true,
      column: {
        size: 240,
        cell: record => {
          return (
            <span className="tw:flex tw:w-full tw:items-center tw:gap-2">
              <span className="tw:flex-1 tw:truncate" title={record.name}>
                {record.name}
              </span>
              <Button
                className="tw:grow-0!"
                variant="outline"
                onClick={e => {
                  e.stopPropagation()
                  rowActionHandlers.onEdit(record.id)
                }}
              >
                {t.edit_verb}
              </Button>
            </span>
          )
        },
      },
      fixed: true,
    },
    {
      key: ResourceFormsColumnId.AssociatedProperties,
      label: t.associatedProperties,
      value: row => row.associatedProperties,
      sortable: true,
      searchable: false,
      column: {
        size: 180,
      },
    },
    {
      key: ResourceFormsColumnId.AppliedToCount,
      label: t.appliedToColumn,
      value: row => row.appliedToCount,
      sortable: true,
      searchable: false,
      column: {
        size: 180,
        cell: record => {
          const count = record.appliedToCount
          return (
            <span
              className={classList({
                'tw:text-body-md': true,
                'tw:text-muted-foreground': count === 0,
              })}
            >
              {t.xResourceObjects(count, record.resourceTypeId)}
            </span>
          )
        },
      },
    },
    {
      key: ResourceFormsColumnId.CreatedBy,
      label: t.createdBy,
      value: row => row.createdBy,
      searchable: false,
      column: {
        size: 180,
        enableSorting: false,
        cell: record => <UserOrBrandCell userId={record.createdBy} />,
      },
    },
    {
      key: ResourceFormsColumnId.UpdatedAt,
      label: t.lastUpdated,
      value: row => row.updatedAt,
      searchable: false,
      column: {
        size: 200,
        enableSorting: false,
      },
    },
    {
      key: ResourceFormsColumnId.UpdatedBy,
      label: t.lastUpdatedBy,
      value: row => row.updatedBy,
      searchable: false,
      column: {
        size: 180,
        enableSorting: false,
        cell: record => <UserOrBrandCell userId={record.updatedBy} />,
      },
    },
  ]

  return buildTableFields({
    getId: row => row.id,
    fields,
  })
}
